import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProductionRouteItem {
  id: number;
  name: string;
}

interface ProductionRouteTranslationFile {
  items?: ProductionRouteItem[];
}

type RouteTranslations = Record<number, string>;

type TranslationLoader = () => Promise<{ default: ProductionRouteTranslationFile }>;

const translationLoaders: Record<string, TranslationLoader> = {
  en: () => import('../locales/ProductionRoutes/en.json'),
  de: () => import('../locales/ProductionRoutes/de.json'),
  fr: () => import('../locales/ProductionRoutes/fr.json'),
  cn: () => import('../locales/ProductionRoutes/cn.json'),
  tr: () => import('../locales/ProductionRoutes/tr.json'),
  es: () => import('../locales/ProductionRoutes/es.json'),
};

const normalizeLanguage = (language: string | undefined): string | undefined => {
  if (!language) {
    return undefined;
  }

  const [base] = language.split('-');
  return base?.toLowerCase();
};

const toTranslationMap = (
  file: ProductionRouteTranslationFile | undefined,
): RouteTranslations => {
  if (!file?.items || !Array.isArray(file.items)) {
    return {};
  }

  return file.items.reduce<RouteTranslations>((acc, item) => {
    if (item.id !== undefined) {
      acc[item.id] = item.name;
    }
    return acc;
  }, {});
};

const loadLocale = async (locale: string): Promise<RouteTranslations | null> => {
  const loader = translationLoaders[locale];
  if (!loader) {
    return null;
  }

  const module = await loader();
  return toTranslationMap(module.default);
};

export const useProductionRouteTranslation = () => {
  const { i18n } = useTranslation();
  const [translations, setTranslations] = useState<RouteTranslations>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTranslations = async () => {
      setIsLoading(true);
      setError(null);

      const normalized = normalizeLanguage(i18n.language);
      const localesToTry: string[] = [];

      if (normalized && translationLoaders[normalized]) {
        localesToTry.push(normalized);
      }

      if (!localesToTry.includes('en') && translationLoaders.en) {
        localesToTry.push('en');
      }

      if (localesToTry.length === 0) {
        localesToTry.push('en');
      }

      try {
        for (const locale of localesToTry) {
          const map = await loadLocale(locale);

          if (map && Object.keys(map).length > 0) {
            if (isMounted) {
              setTranslations(map);
              setIsLoading(false);
            }
            return;
          }
        }

        if (isMounted) {
          setTranslations({});
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error);
          setTranslations({});
          setIsLoading(false);
        }
      }
    };

    fetchTranslations();

    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const translateRouteName = useCallback(
    (routeId?: number | null, fallback?: string): string => {
      if (routeId !== undefined && routeId !== null) {
        const translation = translations[routeId];
        if (translation && translation.trim().length > 0) {
          return translation;
        }
      }

      if (fallback && fallback.trim().length > 0) {
        return fallback;
      }

      if (routeId !== undefined && routeId !== null) {
        return routeId.toString();
      }

      return '';
    },
    [translations],
  );

  return {
    translateRouteName,
    isLoading,
    error,
  };
};

